import { InfoCircleOutlined } from '@ant-design/icons';
import { Alert, Button, Form, Modal, message } from 'antd';
import { useContext, useEffect } from 'react';

import { PictureComponent } from 'app/components/commons/PictureComponent/PictureComponent';
import { FormLegacyRenderProp } from 'app/components/forms/FormLegacyRenderProp';
import { HotelContext } from 'app/context/HotelContext/HotelContext';
import { TaskCenterContext } from 'app/context/TaskCenterContext/TaskCenterContext';
import { useBedCategories } from 'app/hooks/data/rooms/useBedCategories';
import { useExtranetRoomSave } from 'app/hooks/data/useRooms';
import {
  BABY_COT_BED_CATEGORY_ID,
  DAY_PACKAGE_ROOM_CATEGORY_ID,
} from 'app/typings';
import type { ExtranetRoom, ExtranetRoomForm } from 'app/typings/packages';
import { maxRoomCapacity } from 'app/utils/room';

import type { Bed } from '../../../../HotelBuilder/Types';
import { ExtraBed } from '../../ExtraBed';

import './ExtraBedModal.scss';

type Props = {
  bedTypeId: number;
  rooms: ExtranetRoom[];
  isOpen?: boolean;
  onCompleteTask?: () => void;
};

export const ExtraBedModal = ({
  bedTypeId,
  rooms,
  isOpen,
  onCompleteTask,
}: Props) => {
  const [form] = Form.useForm();
  const { hotelId } = useContext(HotelContext);
  const { mutateAsync: saveRooms } = useExtranetRoomSave(hotelId);
  const { data: bedCategories } = useBedCategories();
  const { clearOpenTask } = useContext(TaskCenterContext);

  const bedName =
    bedTypeId === BABY_COT_BED_CATEGORY_ID ? 'baby cot' : 'guest bed';

  const onFinish = async (values: { rooms: ExtranetRoom[] }) => {
    const roomsToSave = {
      rooms: values.rooms.map((room) => {
        return {
          id: room.id,
          beds: room.beds,
        };
      }),
    };

    try {
      await saveRooms(roomsToSave);
      message.success('Rooms saved');
    } catch {
      message.error('Error saving rooms');
    }

    onCompleteTask?.();

    clearOpenTask();
  };

  // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
  const getPrefix = (rooms: ExtranetRoomForm[], index: number) => {
    const listBeds = rooms[index].beds;
    // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
    const bed = listBeds.find((bed: Bed) => bed.categoryId === bedTypeId);

    const bedIndex = bed
      ? listBeds.findIndex((b: Bed) => b.id === bed?.id)
      : listBeds.length - 1;

    return [bedIndex];
  };

  useEffect(() => {
    form.setFieldsValue({ rooms });
  }, [form, rooms]);

  return (
    <FormLegacyRenderProp
      form={form}
      onFinish={onFinish}
      initialValues={{ rooms }}
      preserve={false}
    >
      {(values) => (
        <Modal
          width={558}
          className="extraBedModal"
          bodyStyle={{ maxHeight: 'calc(100vh - 400px)', overflowY: 'auto' }}
          open={isOpen}
          title={
            <div className="extraBedModal__header">
              <h1>Can this room accommodate a {bedName}?</h1>
            </div>
          }
          onCancel={clearOpenTask}
          wrapClassName="extraBedModalContainer"
          centered
          footer={[
            <Button
              size="large"
              type="ghost"
              key="close"
              onClick={clearOpenTask}
            >
              Close
            </Button>,
            <Button
              key="save"
              size="large"
              type="primary"
              htmlType="submit"
              onClick={() => form.submit()}
            >
              Update
            </Button>,
          ]}
          destroyOnClose
        >
          <div className="extraBedModal__body">
            <Alert
              type="info"
              message={`Your ${bedName}s will be set up for all your packages`}
              icon={<InfoCircleOutlined />}
              showIcon
            />
            <p className="subtitle">Your room categories</p>
            <div className="extraBedModal__body_rooms">
              <Form.List name="rooms">
                {(fields) =>
                  fields.map(({ key, name: roomName, ...restField }, index) => {
                    const room = values.rooms[index];
                    const baseBeds = room.beds.filter(
                      (bed) => bed.categoryId !== bedTypeId
                    );
                    const roomCapacity = maxRoomCapacity(
                      baseBeds,
                      bedCategories ?? []
                    );
                    const extraBedCapacity =
                      bedCategories?.find((cat) => cat.id === bedTypeId)
                        ?.capacity || 0;
                    const hasExtraBed = room.beds.some(
                      (bed) => bed.categoryId === bedTypeId
                    );

                    let capacityDescription = `${roomCapacity} guests`;

                    if (hasExtraBed && bedTypeId === BABY_COT_BED_CATEGORY_ID) {
                      capacityDescription = `${roomCapacity} guests + ${extraBedCapacity} baby`;
                    } else if (hasExtraBed) {
                      capacityDescription = `${
                        roomCapacity + extraBedCapacity
                      } guests`;
                    }

                    if (room?.categoryId === DAY_PACKAGE_ROOM_CATEGORY_ID) {
                      return null;
                    }

                    return (
                      <div className="extraBedModal__body_rooms_room" key={key}>
                        <div className="extraBedModal__body_rooms_room_info">
                          <div className="extraBedModal__body_rooms_room_info_image">
                            <PictureComponent
                              pictureId={room?.coverPicture.path}
                            />
                          </div>
                          <div className="extraBedModal__body_rooms_room_info_text">
                            <p className="title">
                              {room?.name || room?.categoryName}
                            </p>
                            <p className="description">
                              {room?.categoryName}
                              {' · '}
                              {capacityDescription}
                            </p>
                          </div>
                        </div>

                        <Form.List name={[key, 'beds']}>
                          {(
                            _bedsFields,
                            { add: addBed, remove: removeBed }
                          ) => (
                            <ExtraBed
                              categoryName={
                                bedTypeId === BABY_COT_BED_CATEGORY_ID
                                  ? 'Baby cot'
                                  : 'Guest bed'
                              }
                              categoryId={bedTypeId}
                              bed={room?.beds?.find(
                                (bed: Bed) => bed.categoryId === bedTypeId
                              )}
                              listBed={room?.beds || []}
                              add={addBed}
                              remove={removeBed}
                              previousPrefixes={['rooms', roomName, 'beds']}
                              prefix={getPrefix(values.rooms, index)}
                              switchChecked={room.beds.some(
                                (bed) => bed.categoryId === bedTypeId
                              )}
                            />
                          )}
                        </Form.List>
                      </div>
                    );
                  })
                }
              </Form.List>
            </div>
          </div>
        </Modal>
      )}
    </FormLegacyRenderProp>
  );
};
